import { type ApiSchemas, call } from '@rental/contracts/client'
import { format, formatDate, formatDateTime, localized, plural } from '@rental/i18n'
import { Checkbox, cn, Field } from '@rental/ui'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { Car, SearchX } from 'lucide-react'
import { useId, useRef } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { EmptyState } from '../components/EmptyState'
import { LookupProblem } from '../components/LookupProblem'
import { PageHeader } from '../components/PageHeader'
import { ListRow, ResultCount, ResultList, ROW_LINK } from '../components/Results'
import { SearchField, SearchForm, useSearchFields } from '../components/SearchForm'
import { type ChipTone, StatusChip } from '../components/StatusChip'
import { Guard, LoadState } from '../components/states'
import { StaffSelect } from '../components/ui'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'
import { useRomeToday } from '../rome-today'
import type { VehiclesSearch } from '../router'

const route = getRouteApi('/vehicles')

/**
 * What a vehicle is doing right now, as a chip (DESIGN.md, "A screen and its
 * parts"): free is good news; booked or out with a customer is information;
 * out of service, for cleaning, the workshop or a block, wants a hand; and a
 * retired vehicle has simply left the fleet. No state is something wrong by
 * itself, so none is red.
 */
export const STATE_TONE: Readonly<Record<ApiSchemas['VehicleRow']['state'], ChipTone>> = {
  available: 'success',
  reserved: 'info',
  on_rent: 'info',
  turnaround: 'warning',
  maintenance: 'warning',
  blocked: 'warning',
  retired: 'neutral',
}

export function VehicleState({ state }: { state: ApiSchemas['VehicleRow']['state'] }) {
  const { messages } = useLocale()
  return <StatusChip tone={STATE_TONE[state]}>{messages.vehicleStates[state]}</StatusChip>
}

/**
 * The fleet, for whoever reads the fleet (UI-2g): anyone else who types its address reads
 * the refusal under its title, and nothing is asked of the server for it.
 */
export function VehiclesScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  return (
    <Guard allowed={can(access, 'fleet.read')} title={m.vehicles.title}>
      <VehiclesList />
    </Guard>
  )
}

/** FLT-2 and FLT-5: the fleet, and what each vehicle is doing right now. */
function VehiclesList() {
  const { locale, messages: m } = useLocale()
  const today = useRomeToday()
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const asked = { q: search.q ?? '' }
  // Back, or a link to another search: the field follows the address.
  const [form, setForm] = useSearchFields(asked)
  const list = useQuery({
    queryKey: ['vehicles', search],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/vehicles', {
            params: {
              query: {
                ...(search.category ? { categoryId: search.category } : {}),
                ...(search.q ? { q: search.q } : {}),
                status: search.retired ? 'retired' : 'active',
              },
            },
          }),
        ),
      ),
    placeholderData: keepPreviousData,
  })
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => load(call(api.GET('/v1/categories'))),
    staleTime: Number.POSITIVE_INFINITY,
  })
  const problem = problemOf(list.error)
  const categoriesProblem = problemOf(categories.error)
  const data = list.data
  const fetching = list.isPlaceholderData
  const filtered = search.q !== undefined || search.category !== undefined || search.retired
  const countId = useId()
  const categorySelect = useRef<HTMLSelectElement>(null)

  // The category and the retired switch search at once, and take what was typed with them:
  // the list never shows a search the form does not.
  const go = (change: Partial<VehiclesSearch>) =>
    void navigate({
      to: '/vehicles',
      search: { ...search, q: form.q.trim() || undefined, ...change },
    })

  return (
    <>
      <PageHeader title={m.vehicles.title}>
        <div className="flex flex-col gap-4 pt-2">
          <SearchForm
            label={m.vehicles.title}
            filtered={filtered === true}
            onSubmit={() => go({})}
            onClear={() => void navigate({ to: '/vehicles', search: {} })}
            options={
              <Checkbox
                label={m.vehicles.retired}
                checked={search.retired === true}
                onChange={(event) => go({ retired: event.target.checked ? true : undefined })}
              />
            }
          >
            <SearchField
              label={m.vehicles.search}
              hint={m.vehicles.searchHint}
              value={form.q}
              onChange={(q) => setForm({ q })}
            />
            <Field label={m.vehicles.category} className="grow sm:max-w-xs">
              {(control) => (
                <StaffSelect
                  {...control}
                  ref={categorySelect}
                  value={search.category ?? ''}
                  onChange={(event) => go({ category: event.target.value || undefined })}
                >
                  <option value="">{m.vehicles.allCategories}</option>
                  {categories.data?.items.map((category) => (
                    <option key={category.id} value={category.id}>
                      {localized(category.name, locale)}
                    </option>
                  ))}
                </StaffSelect>
              )}
            </Field>
          </SearchForm>
          {/*
            Without its categories the select offers only "all": say why, and a way to ask again.
            Once they come, the notice goes and the focus goes to them.
          */}
          <LookupProblem
            problem={categoriesProblem}
            what={m.vehicles.categoriesFailed}
            busy={categories.isFetching}
            retry={categories.refetch}
            target={categorySelect}
          />
          <ResultCount
            id={countId}
            count={data ? plural(locale, m.vehicles.found, data.items.length) : null}
            range={null}
            asked={JSON.stringify(search)}
            fetching={fetching}
          />
        </div>
      </PageHeader>

      <LoadState data={data} problem={problem}>
        {(data) =>
          data.items.length === 0 ? (
            filtered ? (
              <EmptyState icon={SearchX} title={m.vehicles.none}>
                {m.lists.change}
              </EmptyState>
            ) : (
              <EmptyState icon={Car} title={m.vehicles.none}>
                {m.vehicles.fills}
              </EmptyState>
            )
          ) : (
            <ResultList countId={countId} fetching={fetching}>
              {data.items.map((vehicle) => (
                <ListRow
                  key={vehicle.id}
                  title={
                    <>
                      {/* The link's name is the plate alone: it is how the counter says a car. */}
                      <Link
                        to="/vehicles/$vehicleId"
                        params={{ vehicleId: vehicle.id }}
                        className={cn('figures font-bold', ROW_LINK)}
                      >
                        {vehicle.plate}
                      </Link>
                      <span className="font-medium">
                        {vehicle.make} {vehicle.model}
                      </span>
                    </>
                  }
                  end={<VehicleState state={vehicle.state} />}
                >
                  <span>{localized(vehicle.category.name, locale)}</span>
                  <span>
                    {vehicle.nextBooking
                      ? format(m.vehicles.next, {
                          code: vehicle.nextBooking.code,
                          date: formatDateTime(locale, vehicle.nextBooking.startsAt),
                        })
                      : m.vehicles.noNext}
                  </span>
                  {vehicle.nextExpiry ? (
                    // A day gone by is said as gone, in red, as on the vehicle's page (UI-2g):
                    // "scade il" before a past date read as a day still to come.
                    vehicle.nextExpiry.expiresOn < today ? (
                      <span>
                        <StatusChip tone="danger" compact>
                          {format(m.vehicles.expired[vehicle.nextExpiry.kind], {
                            date: formatDate(locale, vehicle.nextExpiry.expiresOn, 'short'),
                          })}
                        </StatusChip>
                      </span>
                    ) : (
                      <span>
                        {format(m.vehicles.expiry, {
                          kind: m.vehicleDocuments[vehicle.nextExpiry.kind],
                          date: formatDate(locale, vehicle.nextExpiry.expiresOn, 'short'),
                        })}
                      </span>
                    )
                  ) : null}
                </ListRow>
              ))}
            </ResultList>
          )
        }
      </LoadState>
    </>
  )
}
